// Shared marketplace CVO helpers — no React, no feature-module imports.
// Consumed by add-agent, agent-creation-sidecar, gallery, home-page, licenses, and app.
import { apiClient } from "utils/clients";
import externals from "utils/externals";
import { buildMarketplaceAssociateAddAgentLocation } from "utils/addAgentUtil";

export const SUBSCRIPTION_MAPPING_PATH = "/licenses-and-subscriptions/subscription-mapping";
export const SUBSCRIPTIONS_OVERVIEW_PATH = "/licenses-and-subscriptions/overview";

export const CVO_PRODUCT_SERVICE_NAME = "cvo-product";

export const AGENTS_MGMT_PATH = "/agents-mgmt";
export const ADD_AGENT_PATH = "/agents-mgmt/add-agent";
export const QUICK_ADD_AGENT_PATH = "/agents-mgmt/quick-add-agent";
export const HOME_PATH = "/home";
export const WORKING_ENVIRONMENTS_PATH = "/working-environments";
export const CHOOSE_WORKING_ENVIRONMENT_TYPE_PATH = "/add-working-environment/choose-type";
export const CVO_AWS_QUICK_CREATE_PATH = "/add-new-working-environment/cvo/aws";
export const CVO_QUICK_CREATE_PATHS = {
    aws: CVO_AWS_QUICK_CREATE_PATH,
    azure: "/add-new-working-environment/cvo/azure",
    gcp: "/add-new-working-environment/cvo/gcp",
};

// Cloud providers whose cvo-product marketplace context drives the NSL flow (association redirect,
// post-agent-deployment redirect to canvas, popup, CVO quick create).
export const NSL_MARKETPLACE_PROVIDERS = ["aws", "azure", "gcp"];

export const isMarketplaceCvoSupportedProvider = (provider) =>
    NSL_MARKETPLACE_PROVIDERS.includes(provider?.toLowerCase());

export const CLOUD_PROVIDER_DISPLAY_NAMES = {
    aws: "AWS",
    azure: "Azure",
    gcp: "Google Cloud",
};

export const getCloudProviderDisplayName = (provider) =>
    CLOUD_PROVIDER_DISPLAY_NAMES[provider?.toLowerCase()];

export const isCvoProductServiceName = (serviceName) =>
    serviceName?.toLowerCase() === CVO_PRODUCT_SERVICE_NAME;

export const selectHasMarketplaceContext = (state) => !!state?.marketplaceContext?.row;

export const isMarketplaceProductAssociated = (liveState) =>
    liveState?.isProductAssociatedInDw === true;

export const resolveMarketplaceContextParams = ({ marketplaceRow, redirection, fallbackCloudProvider = "aws" }) => ({
    cloudProvider: (redirection?.cloudProvider || marketplaceRow?.cloudProvider || fallbackCloudProvider).toLowerCase(),
    serviceName: (redirection?.serviceName || marketplaceRow?.serviceName)?.toLowerCase() ?? null,
});

export const findActiveAgentForProvider = (agents, cloudProvider) => {
    const provider = cloudProvider?.toLowerCase();
    if (!provider) {
        return null;
    }
    return (agents || []).find(a => a.provider === provider && a.status === "active") || null;
};

export const getPreferredActiveAgent = (selectedAgent, agents, cloudProvider) => {
    const provider = cloudProvider?.toLowerCase();
    if (!provider) {
        return null;
    }
    if (selectedAgent?.provider === provider && selectedAgent?.status === "active") {
        return selectedAgent;
    }
    return findActiveAgentForProvider(agents, provider);
};

export const getAddAgentExitPath = (provider, marketplaceRow) => {
    const normalizedProvider = provider?.toLowerCase();
    const marketplaceProvider = marketplaceRow?.cloudProvider?.toLowerCase();
    return marketplaceProvider && normalizedProvider === marketplaceProvider ? HOME_PATH : AGENTS_MGMT_PATH;
};

// Default stays AWS so existing no-arg callers keep the master path.
// A provider argument selects that cloud's CVO quick-create page.
export const buildCvoCreateLocation = (cloudProvider = "aws") => ({
    pathname: CVO_QUICK_CREATE_PATHS[cloudProvider?.toLowerCase()] || CVO_AWS_QUICK_CREATE_PATH,
});

export const buildMarketplaceAssociationPath = (cloudProvider) =>
    `${SUBSCRIPTION_MAPPING_PATH}?${new URLSearchParams({ cloudProvider, serviceName: CVO_PRODUCT_SERVICE_NAME }).toString()}`;

export const buildResumeAddAgentLocation = (cloudProvider) => {
    const provider = cloudProvider?.toLowerCase();
    return {
        pathname: ADD_AGENT_PATH,
        ...(provider === "aws" ? { hash: "#aws" } : {}),
        state: {
            selectedProvider: provider,
            navigatingFrom: {
                pageName: "licenses_marketplace_aws",
                routePath: WORKING_ENVIRONMENTS_PATH,
            },
        },
    };
};

export const buildQuickAddAgentLocation = (cloudProvider) => ({
    pathname: `${QUICK_ADD_AGENT_PATH}/${cloudProvider?.toLowerCase()}`,
});

export const selectCvoProductContext = (contexts, cloudProvider) => {
    const provider = cloudProvider?.toLowerCase();
    return (contexts || []).find(c =>
        isCvoProductServiceName(c?.row?.serviceName) &&
        (!provider || c?.row?.cloudProvider?.toLowerCase?.() === provider)
    ) ?? null;
};

export const hasCvoProductContextForProvider = (contexts, cloudProvider) =>
    !!selectCvoProductContext(contexts, cloudProvider);

// Token-less resume has no resolve-{provider} payload; reconstruct the subscription id from the
// preserved marketplace context (same shape the subscription service uses for every provider — see
// MarketplaceContextService#computeIsProductAssociatedInDw) so auto-associate / redirect can proceed
// instead of hanging. Provider-generic: works for aws/azure/gcp as long as the saved context carries
// userContext.productCode + customerIdentifier (the subscription service maps each provider's own
// identifiers — e.g. Azure offerId/subscriptionId, GCP productCode/googleAccountId — onto those same
// two fields when it persists the row).
export const deriveMarketplaceContextSubscriptionId = (marketplaceRow) => {
    const productCode = marketplaceRow?.userContext?.productCode;
    const customerIdentifier = marketplaceRow?.customerIdentifier;
    const rowProvider = marketplaceRow?.cloudProvider?.toLowerCase();
    if (!productCode || !customerIdentifier || !rowProvider) {
        return null;
    }
    return `${rowProvider}-${productCode}-${customerIdentifier}`;
};

const isNslMarketplaceProvider = (cloudProvider) =>
    NSL_MARKETPLACE_PROVIDERS.includes(cloudProvider?.toLowerCase());

export const isCvoProductResumeRoute = ({ cloudProvider, serviceName, error } = {}) =>
    !error && isNslMarketplaceProvider(cloudProvider) && isCvoProductServiceName(serviceName);

const isCvoProductMarketplaceFlowForProvider = ({ cloudProvider, serviceName, marketplaceRow } = {}) => {
    const normalizedProvider = cloudProvider?.toLowerCase();
    const contextProvider = marketplaceRow?.cloudProvider?.toLowerCase();
    const fromUrl =
        isNslMarketplaceProvider(normalizedProvider) && isCvoProductServiceName(serviceName);
    // When the URL names a different product for this provider, ignore stored cvo-product context.
    const urlOverridesContext =
        isNslMarketplaceProvider(normalizedProvider) &&
        serviceName &&
        !isCvoProductServiceName(serviceName);
    const fromContext =
        (!normalizedProvider || isNslMarketplaceProvider(normalizedProvider)) &&
        !urlOverridesContext &&
        hasCvoProductContextForProvider(
            marketplaceRow ? [{ row: marketplaceRow }] : [],
            normalizedProvider || contextProvider
        );
    return fromUrl || fromContext;
};

export const isAwsCvoProductMarketplaceFlow = ({ cloudProvider, serviceName, marketplaceRow } = {}) => {
    const normalizedProvider = cloudProvider?.toLowerCase();
    const fromUrl =
        normalizedProvider === "aws" && isCvoProductServiceName(serviceName);
    // When the URL names a different AWS product, ignore stored cvo-product context.
    const urlOverridesContext =
        normalizedProvider === "aws" &&
        serviceName &&
        !isCvoProductServiceName(serviceName);
    const fromContext =
        (!normalizedProvider || normalizedProvider === "aws") &&
        !urlOverridesContext &&
        hasCvoProductContextForProvider(
            marketplaceRow ? [{ row: marketplaceRow }] : [],
            "aws"
        );
    return fromUrl || fromContext;
};

/** NSL (`cvo-product`) UX for AWS, Azure, and GCP. */
export const isCvoProductSimplificationFlow = (params = {}) =>
    isCvoProductMarketplaceFlowForProvider(params);

// Agent deployment completed for the provider of the active marketplace context — the deployment
// should land on canvas with the post-agent-deployment popup.
export const isMarketplaceCvoDeployCompletionFlow = ({ marketplaceRow, selectedProvider }) => {
    const provider = selectedProvider?.toLowerCase();
    return isMarketplaceCvoSupportedProvider(provider)
        && !!marketplaceRow
        && marketplaceRow.cloudProvider?.toLowerCase() === provider;
};

// Providers for which the agent creation sidecar is supported.
export const SIDECAR_SUPPORTED_PROVIDERS = NSL_MARKETPLACE_PROVIDERS;

/**
 * Whether the setup-guide sidecar accompanies an agent deployment page (add-agent wizard or quick
 * mode) for `provider`. Shown only when the page provider matches the marketplace context provider.
 */
export const shouldShowSidecar = (provider, marketplaceState, agents = []) => {
    const marketplaceProvider = marketplaceState?.row?.cloudProvider?.toLowerCase();
    if (!marketplaceProvider
        || !SIDECAR_SUPPORTED_PROVIDERS.includes(marketplaceProvider)
        || provider !== marketplaceProvider) {
        return false;
    }
    // Hide only when an ACTIVE agent already exists — a pending/failed agent still shows the guide.
    return !findActiveAgentForProvider(agents, marketplaceProvider);
};

/** Supported-provider cvo-product marketplace context is present (canvas guide flow may apply). */
export const isCvoProductMarketplaceCanvasContext = (marketplaceState) => {
    const row = marketplaceState?.row;
    if (!row) {
        return false;
    }
    return SIDECAR_SUPPORTED_PROVIDERS.includes(row.cloudProvider?.toLowerCase())
        && isCvoProductServiceName(row.serviceName);
};

/**
 * Show the Cloud Volumes ONTAP setup workflow on canvas in place of SummaryPanel (2nd column), whenever
 * a cvo-product marketplace context is active and there is nothing else to show on canvas.
 *
 * Visibility rules (no clouds on canvas), where "marketplace provider" is the context's provider:
 * - Selected agent is another provider → hide (even if a pending marketplace-provider agent exists).
 * - Selected agent is the marketplace provider (pending or active) → show. Deploy-agent step status
 *   inside the sidecar distinguishes pending (WARNING) from active (SUCCESS) via `getPreferredActiveAgent`.
 * - No agent selected: pending/inactive marketplace-provider agents count as "no agent present" → show,
 *   same as an empty agents list. An active agent of another provider with no selection → hide.
 *
 * The active/pending distinction for individual step content is handled inside the sidecar itself.
 */
export const shouldShowCanvasMarketplaceSidecar = ({
    marketplaceState,
    hasWorkingEnvironments = false,
    selectedAgent,
    agents,
} = {}) => {
    if (!isCvoProductMarketplaceCanvasContext(marketplaceState) || hasWorkingEnvironments) {
        return false;
    }

    const marketplaceProvider = marketplaceState.row.cloudProvider?.toLowerCase();
    const selectedProvider = selectedAgent?.provider?.toLowerCase();

    if (selectedProvider && selectedProvider !== marketplaceProvider) {
        return false;
    }
    if (selectedProvider === marketplaceProvider) {
        return true;
    }

    if (!(agents || []).length) {
        return true;
    }

    // Pending/non-active marketplace-provider agents are treated as no agent present for guide visibility.
    if (!findActiveAgentForProvider(agents, marketplaceProvider)) {
        const hasActiveNonMarketplaceAgent = (agents || []).some(
            (agent) => agent.status === "active" && agent.provider?.toLowerCase() !== marketplaceProvider
        );
        return !hasActiveNonMarketplaceAgent;
    }

    return false;
};

export const isAgentsListReady = (agentStatus) =>
    !!agentStatus && agentStatus !== "PENDING";

/**
 * True once `hasWorkingEnvironments` can be trusted to decide between the canvas guide and the
 * gallery — i.e. the agents list has finished its initial load AND the gallery's initial fetch
 * has settled. Guards against a transient "looks empty" read before either has actually loaded,
 * which would otherwise flash the guide (or the empty-state) before the real canvas content.
 */
export const isMarketplaceCanvasFetchSettled = ({
    marketplaceState,
    agentStatus,
    initialLoadComplete = false,
    initialDataFetchComplete = false,
    fetchInProgress = false,
} = {}) => {
    if (!isCvoProductMarketplaceCanvasContext(marketplaceState)) {
        return true;
    }
    if (!isAgentsListReady(agentStatus)) {
        return false;
    }
    return initialLoadComplete && initialDataFetchComplete && !fetchInProgress;
};

export const shouldDeferMarketplaceCanvasContent = (params) =>
    isCvoProductMarketplaceCanvasContext(params?.marketplaceState)
    && !isMarketplaceCanvasFetchSettled(params);

/**
 * Pure decision function backing App.js's marketplace-provider auto-select effect. Auto-selects the
 * marketplace context's provider agent exactly once per context, right when the initial agents load
 * (login) settles — never again afterwards, even if the same context is re-evaluated later (e.g. a
 * canvas revisit) or an agent that was missing at login becomes active later. That one-shot-at-login
 * window is intentional: post-association and Resume-button flows already select the agent
 * explicitly at the right moment (see other `ensureSelectedAgent` call sites), so this must not also
 * retry in the background — doing so could silently override a manual switch to a different provider
 * made in between.
 *
 * Callers own persisting `nextContextKey` (e.g. in a `useRef`) across calls and passing it back in
 * as `resolvedContextKey` next time.
 *
 * @returns {{ nextContextKey: string|null, agentToSelect: object|null }}
 */
export const resolveMarketplaceAutoSelect = ({ row, agentStatus, agents, resolvedContextKey = null } = {}) => {
    if (!row) {
        return { nextContextKey: null, agentToSelect: null };
    }

    const contextKey = `${row.cloudProvider}:${row.serviceName}`;
    if (resolvedContextKey === contextKey) {
        return { nextContextKey: resolvedContextKey, agentToSelect: null };
    }
    if (!agentStatus || agentStatus === "PENDING") {
        // Initial agents load (login) hasn't settled yet — wait for it instead of resolving
        // (and permanently closing the window) on a still-empty/incomplete agents list.
        return { nextContextKey: resolvedContextKey, agentToSelect: null };
    }

    // This is the one and only resolution point for this context: mark it handled now, regardless
    // of outcome, so a later background change in `agents` can never reopen it.
    const marketplaceProvider = row.cloudProvider?.toLowerCase();
    const agentToSelect = findActiveAgentForProvider(agents, marketplaceProvider);
    return { nextContextKey: contextKey, agentToSelect };
};

export const ensureSelectedAgent = (dispatch, selectedAgent, activeAgent) => {
    if (!activeAgent || selectedAgent?.agentId === activeAgent.agentId) {
        return;
    }
    dispatch({ type: "TENANCY:SET-AGENT", payload: { agentId: activeAgent.agentId } });
};

export const getActiveAgentUserIdForCvoCheck = (tenancyState, activeAgent, selectedAgent) =>
    tenancyState?.userAgentMap?.[activeAgent.agentId]
    || (selectedAgent?.agentId === activeAgent.agentId ? tenancyState?.selectedAgentUserId : undefined);

export const fetchCvoExistsUnderAgent = ({ selectedOrgId, selectedAgentUserId }) => {
    if (!selectedAgentUserId || !selectedOrgId) {
        return Promise.resolve(false);
    }
    const filter = `tags.internal:bxp:agentId eq '${selectedAgentUserId}' and resourceClass eq 'VSA'`;
    return apiClient
        .get(`${externals.tenancyV4Backend}/organizations/${selectedOrgId}/resources`, { params: { filter, limit: 1 } })
        .then(({ data }) => (data?.items || []).length > 0)
        .catch(e => {
            // Log only the message — the raw error may carry request headers (e.g. the Authorization
            // bearer token), which must not be printed to the browser console.
            console.error("[MARKETPLACE-CONTEXT] CVO existence check failed", e?.message);
            throw e;
        });
};

export const MARKETPLACE_RESUME_STEP = {
    ASSOCIATE: "associate",
    ADD_AGENT: "add-agent",
    CVO_CREATE: "cvo-create",
};

export const resolveMarketplaceResumeStep = ({ row, liveState, agents }) => {
    const cloudProvider = row?.cloudProvider?.toLowerCase();
    if (!cloudProvider) {
        return null;
    }

    const isAssociated = isMarketplaceProductAssociated(liveState);
    const hasFailure = !!row?.failure;

    if (!isAssociated && !hasFailure) {
        return { step: MARKETPLACE_RESUME_STEP.ASSOCIATE, cloudProvider };
    }
    if (!isAssociated && hasFailure) {
        return { step: MARKETPLACE_RESUME_STEP.ADD_AGENT, cloudProvider };
    }

    const activeAgent = findActiveAgentForProvider(agents, cloudProvider);
    if (!activeAgent) {
        return { step: MARKETPLACE_RESUME_STEP.ADD_AGENT, cloudProvider };
    }

    return { step: MARKETPLACE_RESUME_STEP.CVO_CREATE, cloudProvider, activeAgent };
};

export const applyMarketplaceResumeStep = ({ stepResult, dispatch, history, selectedAgent }) => {
    if (!stepResult) {
        return;
    }
    const { step, cloudProvider, activeAgent } = stepResult;
    switch (step) {
        case MARKETPLACE_RESUME_STEP.ASSOCIATE:
            history.push(buildMarketplaceAssociationPath(cloudProvider));
            break;
        case MARKETPLACE_RESUME_STEP.ADD_AGENT:
            history.push(buildMarketplaceAssociateAddAgentLocation(cloudProvider));
            break;
        case MARKETPLACE_RESUME_STEP.CVO_CREATE:
            ensureSelectedAgent(dispatch, selectedAgent, activeAgent);
            history.push(buildCvoCreateLocation(cloudProvider));
            break;
        default:
            break;
    }
};
